import React, { useState, useMemo } from 'react';
import { Product, CreateProductFormData, ProductType } from '../../types/product';
import { useProducts } from '../../hooks/useProducts';
import {
  CubeIcon,
  WrenchScrewdriverIcon,
  PlusIcon,
  FunnelIcon,
  TagIcon,
  BuildingOffice2Icon,
  PencilSquareIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import SearchBar from '../common/SearchBar';
import { useOrganizations } from '../../hooks/useOrganizations';
import ProductForm from './ProductForm';
import { Tooltip } from '../common/Tooltip';
import { useLanguage } from 'contexts/LanguageContext';
import Pagination from '../common/Pagination';
import ConfirmDeleteModal from '../../components/common/ConfirmDeleteModal';

interface ProductListProps {
  selectedOrg: string;
  isModalOpen?: boolean;
  setIsModalOpen?: (open: boolean) => void;
}

const ProductList: React.FC<ProductListProps> = ({ 
  selectedOrg, 
  isModalOpen: externalIsModalOpen, 
  setIsModalOpen: externalSetIsModalOpen 
}) => {
  const { products, createProduct, updateProduct, deleteProduct } = useProducts();
  const { organizations } = useOrganizations();
  const [typeFilter, setTypeFilter] = useState<ProductType | 'all'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [internalIsModalOpen, setInternalIsModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<Product | null>(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { t } = useLanguage();

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Use external state if provided, otherwise use internal state
  const isModalOpen = externalIsModalOpen !== undefined ? externalIsModalOpen : internalIsModalOpen;
  const setIsModalOpen = externalSetIsModalOpen || setInternalIsModalOpen;

  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      const matchesSearch =
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesType = typeFilter === 'all' || product.type === typeFilter;

      const matchesOrg = selectedOrg === 'all' ||
        product.organizationProducts.some(op => op.organizationId === selectedOrg);

      return matchesSearch && matchesType && matchesOrg;
    });
  }, [products, searchTerm, typeFilter, selectedOrg]);

  // Pagination logic
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentProducts = filteredProducts.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const handleCreateProduct = async (data: CreateProductFormData) => {
    try {
      await createProduct(data);
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error creating product:', error);
    }
  };

  const handleUpdateProduct = async (data: CreateProductFormData) => {
    if (!selectedProduct) return;
    try {
      await updateProduct(selectedProduct.id, data);
      setIsModalOpen(false);
      setSelectedProduct(null);
    } catch (error) {
      console.error('Error updating product:', error);
    }
  };

  const handleEditClick = (product: Product) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
  };

  const getInitialFormData = (product: Product): CreateProductFormData => ({
    type: product.type,
    code: product.code,
    name: product.name,
    description: product.description,
    unit: product.unit,
    isActive: product.isActive,
    organizations: product.organizationProducts.map((op) => ({
      organizationId: op.organizationId,
      price: op.price,
      currency: op.currency,
      taxRate: op.taxRate,
      validFrom: op.validFrom,
      validUntil: op.validUntil,
      discounts: op.discounts
    }))
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="w-full sm:flex-1">
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder={t('common.placeholders.selectProduct')}
          />
        </div>
      </div>

      <div className="hidden lg:block bg-white rounded-lg overflow-hidden shadow-sm border border-gray-200">
        <div className="flex flex-wrap items-center justify-between p-3 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row w-full items-start sm:items-center justify-between gap-2">
            {/* Mobile search and filter toggle */}
            <div className="flex sm:hidden w-full">
              <div className="relative flex-grow">
                <input
                  type="text"
                  placeholder={t('common.placeholders.selectProduct')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-white border border-gray-300 rounded-md py-1.5 pl-3 pr-8 text-sm text-gray-700 focus:ring-1 focus:ring-primary-500"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                  <MagnifyingGlassIcon className="h-4 w-4 text-gray-500" />
                </div>
              </div>
              <button
                className="ml-2 bg-gray-200 p-1.5 rounded-md text-gray-600"
                onClick={() => setIsFilterOpen(!isFilterOpen)}
              >
                <FunnelIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Mobile filters dropdown */}
            {isFilterOpen && (
              <div className="sm:hidden w-full mt-2 flex flex-wrap gap-2">
                <div className="flex flex-wrap gap-1 sm:gap-2 w-full">
                  <button
                    onClick={() => setTypeFilter('all')}
                    className={`inline-flex items-center px-2 py-1.5 rounded-lg text-xs font-medium
                      ${typeFilter === 'all'
                        ? 'border-teal-500 bg-teal-500/20 text-teal-600'
                        : 'border border-gray-300 text-gray-600 hover:border-gray-400'
                      }`}
                  >
                    <FunnelIcon className="w-3.5 h-3.5 mr-1" />
                    {t('products.filter.allTypes')}
                  </button>
                  <button
                    onClick={() => setTypeFilter('product')}
                    className={`inline-flex items-center px-2 py-1.5 rounded-lg text-xs font-medium
                      ${typeFilter === 'product'
                        ? 'border-blue-500 bg-blue-500/20 text-blue-600'
                        : 'border border-gray-300 text-gray-600 hover:border-gray-400'
                      }`}
                  >
                    <CubeIcon className="w-3.5 h-3.5 mr-1" />
                    {t('products.filter.products')}
                  </button>
                  <button
                    onClick={() => setTypeFilter('service')}
                    className={`inline-flex items-center px-2 py-1.5 rounded-lg text-xs font-medium
                      ${typeFilter === 'service'
                        ? 'border-purple-500 bg-purple-500/20 text-purple-600'
                        : 'border border-gray-300 text-gray-600 hover:border-gray-400'
                      }`}
                  >
                    <WrenchScrewdriverIcon className="w-3.5 h-3.5 mr-1" />
                    {t('products.filter.services')}
                  </button>
                </div>
              </div>
            )}

            {/* Desktop filter buttons */}
            <div className="hidden sm:flex items-center gap-1.5 overflow-x-auto pb-2 scrollbar-none">
              <button
                onClick={() => setTypeFilter('all')}
                className={`flex-none inline-flex items-center px-2 py-1.5 rounded-lg border text-xs whitespace-nowrap
                  ${typeFilter === 'all'
                    ? 'border-teal-500 bg-teal-500/20 text-teal-600'
                    : 'border-gray-300 text-gray-600 hover:border-gray-400'
                  }`}
              >
                <FunnelIcon className="w-3.5 h-3.5 mr-1" />
                {t('products.filter.allTypes')}
              </button>
              <button
                onClick={() => setTypeFilter('product')}
                className={`flex-none inline-flex items-center px-2 py-1.5 rounded-lg border text-xs whitespace-nowrap
                  ${typeFilter === 'product'
                    ? 'border-blue-500 bg-blue-500/20 text-blue-600'
                    : 'border-gray-300 text-gray-600 hover:border-gray-400'
                  }`}
              >
                <CubeIcon className="w-3.5 h-3.5 mr-1" />
                {t('products.filter.products')}
              </button>
              <button
                onClick={() => setTypeFilter('service')}
                className={`flex-none inline-flex items-center px-2 py-1.5 rounded-lg border text-xs whitespace-nowrap
                  ${typeFilter === 'service'
                    ? 'border-purple-500 bg-purple-500/20 text-purple-600'
                    : 'border-gray-300 text-gray-600 hover:border-gray-400'
                  }`}
              >
                <WrenchScrewdriverIcon className="w-3.5 h-3.5 mr-1" />
                {t('products.filter.services')}
              </button>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('products.fields.product')}</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('products.fields.organizations')}</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('products.fields.price')}</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-20">{t('products.fields.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {currentProducts.map(product => (
                <tr key={product.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2">
                    <div className="flex items-center space-x-3">
                      <div className={`p-1 rounded bg-gradient-to-br ${product.type === 'product'
                        ? 'from-blue-500/20 to-blue-600/20 border-blue-500/20'
                        : 'from-purple-500/20 to-purple-600/20 border-purple-500/20'
                        } border`}>
                        {product.type === 'product' ? (
                          <CubeIcon className="h-4 w-4 text-blue-600" />
                        ) : (
                          <WrenchScrewdriverIcon className="h-4 w-4 text-purple-600" />
                        )}
                      </div>
                      <div>
                        <div className="text-sm text-gray-800">{product.name}</div>
                        <div className="text-xs text-gray-600 flex items-center">
                          <TagIcon className="h-3 w-3 mr-1" />
                          {product.code}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {product.organizationProducts.map(op => {
                        const org = organizations.find(o => o.id === op.organizationId);
                        return org ? (
                          <span
                            key={org.id}
                            className="inline-flex items-center px-1.5 py-0.5 rounded-sm text-[10px] font-medium bg-gray-100 text-gray-700"
                          >
                            <BuildingOffice2Icon className="w-3 h-3 mr-1" />
                            {org.name}
                          </span>
                        ) : null;
                      })}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div className="space-y-1">
                      {product.organizationProducts.map(op => {
                        const org = organizations.find(o => o.id === op.organizationId);
                        return selectedOrg === 'all' || selectedOrg === op.organizationId ? (
                          <div key={op.organizationId} className="flex items-center justify-end space-x-1 text-xs">
                            <span className="text-gray-600">{org?.name}:</span>
                            <span className="text-gray-800">
                              {op.price.toLocaleString()} {op.currency}
                            </span>
                            <span className="text-gray-500">
                              (+{op.taxRate}{t('products.fields.tax')})
                            </span>
                          </div>
                        ) : null;
                      })}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end space-x-2">
                      <Tooltip content={t('products.tooltips.duplicate')}>
                        <button
                          onClick={() => {
                            const duplicateData: CreateProductFormData = {
                              type: product.type,
                              name: `${product.name} (Copy)`,
                              code: `${product.code}-COPY`,
                              description: product.description,
                              unit: product.unit,
                              isActive: product.isActive,
                              organizations: product.organizationProducts.map(op => ({
                                organizationId: op.organizationId,
                                price: op.price,
                                currency: op.currency,
                                taxRate: op.taxRate
                              }))
                            };
                            createProduct(duplicateData);
                          }}
                          className="text-gray-500 hover:text-blue-600"
                        >
                          <DocumentDuplicateIcon className="h-4 w-4" />
                        </button>
                      </Tooltip>
                      <Tooltip content={t('products.tooltips.edit')}>
                        <button
                          onClick={() => handleEditClick(product)}
                          className="text-gray-500 hover:text-primary-600"
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                        </button>
                      </Tooltip>
                      <Tooltip content={t('products.tooltips.delete')}>
                        <button
                          onClick={() => {
                            setProductToDelete(product);
                            setIsConfirmDeleteOpen(true);
                          }}
                          className="text-gray-500 hover:text-red-600"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </Tooltip>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile cards */}
      <div className="lg:hidden space-y-3 px-1.5">
        {currentProducts.map(product => (
          <div
            key={product.id}
            className={`bg-white rounded-lg border-l-4 ${product.type === 'product' ? 'border-blue-400' : 'border-purple-400'
              } shadow-sm border border-gray-200 w-full overflow-hidden`}
            style={{ maxWidth: 'calc(100vw - 20px)' }}
          >
            <div className="p-2">
              <div className="flex justify-between items-start gap-1">
                <div className="flex items-start flex-1 min-w-0 overflow-hidden mr-1">
                  <div className={`p-1 rounded-md bg-gradient-to-br ${product.type === 'product'
                    ? 'from-blue-500/20 to-blue-600/20 border-blue-500/20'
                    : 'from-purple-500/20 to-purple-600/20 border-purple-500/20'
                    } border mr-1 flex-shrink-0 mt-0.5`}>
                    {product.type === 'product' ? (
                      <CubeIcon className="h-3 w-3 text-blue-600" />
                    ) : (
                      <WrenchScrewdriverIcon className="h-3 w-3 text-purple-600" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1 overflow-hidden">
                    <div className="text-sm font-medium text-gray-800 truncate">{product.name}</div>
                    <div className="text-xs text-gray-600 flex items-center overflow-hidden">
                      <TagIcon className="h-3 w-3 mr-1 flex-shrink-0" />
                      <span className="truncate">{product.code}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-1 mt-1 w-full overflow-hidden">
                {product.organizationProducts.slice(0, 2).map(op => {
                  const org = organizations.find(o => o.id === op.organizationId);
                  return org ? (
                    <span
                      key={org.id}
                      className="inline-flex items-center px-1 py-0.5 rounded text-[9px] font-medium bg-gray-100 text-gray-700 overflow-hidden"
                    >
                      <BuildingOffice2Icon className="w-2 h-2 mr-0.5 flex-shrink-0" />
                      <span className="truncate max-w-[60px]">{org.name}</span>
                    </span>
                  ) : null;
                })}
                {product.organizationProducts.length > 2 && (
                  <span className="inline-flex items-center px-1 py-0.5 rounded text-[9px] font-medium bg-gray-100 text-gray-700">
                    +{product.organizationProducts.length - 2}
                  </span>
                )}
              </div>

              <div className="mt-1 border-t border-gray-200 pt-1">
                {product.organizationProducts
                  .filter(op => selectedOrg === 'all' || selectedOrg === op.organizationId)
                  .slice(0, 2)
                  .map(op => {
                    const org = organizations.find(o => o.id === op.organizationId);
                    return (
                      <div key={op.organizationId} className="flex items-center justify-between mb-0.5 last:mb-0">
                        <span className="text-[9px] text-gray-600 truncate max-w-[40%]">{org?.name}:</span>
                        <div className="text-right">
                          <span className="text-[10px] text-gray-800 font-medium">
                            {op.price.toLocaleString()} {op.currency}
                          </span>
                          <span className="text-[9px] text-gray-500 ml-0.5">
                            (+{op.taxRate}{t('products.fields.tax')})
                          </span>
                        </div>
                      </div>
                    );
                  })}
                {(selectedOrg === 'all' ? product.organizationProducts.length :
                  product.organizationProducts.filter(op => op.organizationId === selectedOrg).length) > 2 && (
                    <div className="text-[9px] text-gray-600 text-center mt-0.5">
                      + {(selectedOrg === 'all' ? product.organizationProducts.length :
                        product.organizationProducts.filter(op => op.organizationId === selectedOrg).length) - 2} {t('common.more')}
                    </div>
                  )}
              </div>
            </div>

            <div className="grid grid-cols-4 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => {
                  const duplicateData: CreateProductFormData = {
                    type: product.type,
                    name: `${product.name} (Copy)`,
                    code: `${product.code}-COPY`,
                    description: product.description,
                    unit: product.unit,
                    isActive: product.isActive,
                    organizations: product.organizationProducts.map(op => ({
                      organizationId: op.organizationId,
                      price: op.price,
                      currency: op.currency,
                      taxRate: op.taxRate
                    }))
                  };
                  createProduct(duplicateData);
                }}
                className="flex flex-col items-center justify-center py-1.5 px-0.5 text-gray-700 hover:text-blue-600 active:bg-gray-100 border-r border-gray-200"
              >
                <DocumentDuplicateIcon className="h-3 w-3 mb-0.5" />
                <span className="text-[8px] font-medium text-center w-full truncate">{t('products.copy')}</span>
              </button>
              <button
                onClick={() => handleEditClick(product)}
                className="flex flex-col items-center justify-center py-1.5 px-0.5 text-gray-700 hover:text-primary-600 active:bg-gray-100 border-r border-gray-200"
              >
                <PencilSquareIcon className="h-3 w-3 mb-0.5" />
                <span className="text-[8px] font-medium text-center w-full truncate">{t('products.edit')}</span>
              </button>
              <button
                onClick={() => {
                  setProductToDelete(product);
                  setIsConfirmDeleteOpen(true);
                }}
                className="flex flex-col items-center justify-center py-1.5 px-0.5 text-gray-700 hover:text-red-600 active:bg-gray-100 border-r border-gray-200"
              >
                <TrashIcon className="h-3 w-3 mb-0.5" />
                <span className="text-[8px] font-medium text-center w-full truncate">{t('products.delete')}</span>
              </button>
              <div className="relative flex justify-center">
                <button
                  onClick={(e) => {
                    const dropdown = e.currentTarget.nextElementSibling as HTMLElement;
                    const allDropdowns = document.querySelectorAll('.dropdown-menu');
                    allDropdowns.forEach(el => {
                      if (el !== dropdown) (el as HTMLElement).style.display = 'none';
                    });
                    dropdown.style.display = dropdown.style.display === 'block' ? 'none' : 'block';
                  }}
                  className="flex flex-col items-center justify-center py-1.5 px-0.5 w-full text-gray-700 hover:text-gray-900 active:bg-gray-100"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-3 h-3 mb-0.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 12.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 18.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Z" />
                  </svg>
                  <span className="text-[8px] font-medium text-center w-full truncate">{t('common.more')}</span>
                </button>
                <div
                  className="dropdown-menu absolute right-0 bottom-full mb-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1 w-24"
                  style={{ display: 'none' }}
                >
                  {/* Üresen hagyva a jövőbeli funkcionalitáshoz */}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination controls */}
      {filteredProducts.length > 0 && (
        <div className="relative z-10">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            itemsPerPage={itemsPerPage}
            totalItems={filteredProducts.length}
            indexOfFirstItem={indexOfFirstItem}
            indexOfLastItem={indexOfLastItem}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        </div>
      )}

      <ProductForm
        isOpen={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setSelectedProduct(null);
        }}
        onSubmit={selectedProduct ? handleUpdateProduct : handleCreateProduct}
        initialData={selectedProduct ? getInitialFormData(selectedProduct) : undefined}
      />

      <ConfirmDeleteModal
        isOpen={isConfirmDeleteOpen}
        onClose={() => {
          setIsConfirmDeleteOpen(false);
          setProductToDelete(null);
        }}
        onConfirm={() => {
          if (productToDelete) {
            deleteProduct(productToDelete.id);
            setIsConfirmDeleteOpen(false);
            setProductToDelete(null);
          }
        }}
        title={t('products.messages.confirmTitle')}
        deleteMessage={productToDelete ?
          `${t('products.messages.deleteQuestion')} "${productToDelete.name}"?` :
          t('products.messages.deleteQuestion')}
        deleteWarning={t('products.messages.deleteWarning')}
      />
    </div>
  );
};

export default ProductList; 